import type { ReactNode } from 'react';

import type { InvoiceExtractionIn } from '@/api/invoices';

/** The full captured-invoice field set (SOR/91-45 documentation facts),
 *  shared verbatim by every editor so the finding-detail preview and the
 *  invoice workbench can't drift apart. Bound directly to `InvoiceExtractionIn`
 *  (the API shape) — empty inputs coerce to null. */

export const DOCUMENT_TYPES: Array<{ value: string; label: string }> = [
  { value: 'invoice', label: 'Invoice' },
  { value: 'estimate', label: 'Estimate' },
  { value: 'statement_of_account', label: 'Statement of account' },
  { value: 'engagement_letter', label: 'Engagement letter' },
  { value: 'unreadable', label: 'Unreadable (image-only)' },
  { value: 'other', label: 'Other' },
];

export const TAX_INDICATIONS: Array<{ value: string; label: string }> = [
  { value: 'amount_shown', label: 'Tax amount itemized' },
  { value: 'stated_included', label: '"Tax included" statement' },
  { value: 'rate_shown', label: 'Rate shown (e.g. "13% HST")' },
  { value: 'none', label: 'Nothing shown' },
];

export const SUPPLY_DESCRIPTION_QUALITIES: Array<{ value: string; label: string }> = [
  { value: 'sufficient', label: 'Sufficient' },
  { value: 'generic', label: 'Generic ("services rendered")' },
  { value: 'missing', label: 'Missing' },
];

/** Value → human label for the enum fields, so read-only views can render
 *  the same wording the editor shows instead of the raw enum value. */
export function labelForOption(
  options: Array<{ value: string; label: string }>,
  value: string | null | undefined,
): string {
  if (!value) return '';
  return options.find((o) => o.value === value)?.label ?? value;
}

/** True when the practitioner has typed at least one invoice fact. Used to
 *  decide whether an assign should persist the label — an empty label
 *  shouldn't create a blank extraction row. */
export function hasAnyInvoiceFact(value: InvoiceExtractionIn): boolean {
  return Object.values(value).some((v) => v !== null && v !== undefined && v !== '');
}

const INPUT =
  'w-full rounded-md border border-input bg-background px-2 py-1 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-royal';

export function InvoiceFactsFields({
  value,
  onChange,
  supplierNameOptions,
}: {
  value: InvoiceExtractionIn;
  onChange: (next: InvoiceExtractionIn) => void;
  /** Known supplier names to offer as a searchable datalist on the
   *  supplier field (e.g. the current run's suppliers). Omit for none. */
  supplierNameOptions?: string[];
}) {
  const set = (patch: Partial<InvoiceExtractionIn>) => onChange({ ...value, ...patch });
  const text = (v: string) => (v.trim() === '' ? null : v);
  const supplierListId = 'invoice-supplier-names';

  return (
    <>
      <div className="grid grid-cols-1 gap-2 text-sm sm:grid-cols-2">
        <Field label="Document type">
          <select
            value={value.document_type ?? ''}
            onChange={(e) =>
              set({
                document_type:
                  (text(e.target.value) as InvoiceExtractionIn['document_type']) ?? null,
              })
            }
            className={INPUT}
          >
            <option value="">—</option>
            {DOCUMENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Invoice #">
          <input
            value={value.invoice_number ?? ''}
            onChange={(e) => set({ invoice_number: text(e.target.value) })}
            className={INPUT}
          />
        </Field>
        <Field label="Supplier (as printed)">
          <input
            value={value.supplier_name_on_invoice ?? ''}
            onChange={(e) => set({ supplier_name_on_invoice: text(e.target.value) })}
            className={INPUT}
            list={supplierNameOptions?.length ? supplierListId : undefined}
          />
          {supplierNameOptions?.length ? (
            <datalist id={supplierListId}>
              {supplierNameOptions.map((name) => (
                <option key={name} value={name} />
              ))}
            </datalist>
          ) : null}
        </Field>
        <Field label="Invoice date">
          <input
            type="date"
            value={value.invoice_date ?? ''}
            onChange={(e) => set({ invoice_date: text(e.target.value) })}
            className={INPUT}
          />
        </Field>
        <Field label="Total (tax-incl.)">
          <input
            inputMode="decimal"
            value={value.invoice_total ?? ''}
            onChange={(e) => set({ invoice_total: text(e.target.value) })}
            className={INPUT}
          />
        </Field>
        <Field label="Supplier BN (as written)">
          <input
            value={value.supplier_bn ?? ''}
            onChange={(e) => set({ supplier_bn: text(e.target.value) })}
            placeholder="123456789RT0001 or partial"
            className={INPUT}
          />
        </Field>
        <Field label="Tax indication">
          <select
            value={value.tax_indication ?? ''}
            onChange={(e) =>
              set({
                tax_indication:
                  (text(e.target.value) as InvoiceExtractionIn['tax_indication']) ?? null,
              })
            }
            className={INPUT}
          >
            <option value="">—</option>
            {TAX_INDICATIONS.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Tax shown">
          <input
            inputMode="decimal"
            value={value.tax_shown ?? ''}
            onChange={(e) => set({ tax_shown: text(e.target.value) })}
            className={INPUT}
          />
        </Field>
        <Field label="Recipient (must match claimant)">
          <input
            value={value.recipient_name ?? ''}
            onChange={(e) => set({ recipient_name: text(e.target.value) })}
            className={INPUT}
          />
        </Field>
        <Field label="Payment terms">
          <input
            value={value.payment_terms ?? ''}
            onChange={(e) => set({ payment_terms: text(e.target.value) })}
            placeholder="Net 30"
            className={INPUT}
          />
        </Field>
        <Field label="Supply description">
          <select
            value={value.supply_description_quality ?? ''}
            onChange={(e) =>
              set({
                supply_description_quality:
                  (text(e.target.value) as InvoiceExtractionIn['supply_description_quality']) ??
                  null,
              })
            }
            className={INPUT}
          >
            <option value="">—</option>
            {SUPPLY_DESCRIPTION_QUALITIES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <Field label="Notes">
        <textarea
          value={value.notes ?? ''}
          onChange={(e) => set({ notes: text(e.target.value) })}
          rows={2}
          className={INPUT}
        />
      </Field>
    </>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <label className="grid gap-1 text-xs">
      <span className="uppercase tracking-wide text-brand-slate">{label}</span>
      {children}
    </label>
  );
}
